import {
  AccountInfo,
  CivicSignProve,
  CivicSignProveFactory,
  SignedProof,
  SolanaWalletAdapter,
} from '@civic/civic-sign';
import { clusterApiUrl } from '@solana/web3.js';
import { CustomError, TechnicalError } from '../../common/errors';
import Result from '../../common/result';
import { Signer } from './signer';
import { Nonce } from '../authentication/authenticationService';
import Log from '../../common/logging';
import ErrorWithCode from '../../common/errors/errorWithCode';

const CIVIC_SIGNER_TIMEOUT_MILLISECONDS = 60 * 1000;

export default class CivicSigner implements Signer {
  private signer: CivicSignProve;

  constructor(instanceId: string, chainName: 'solana' | 'ethereum' | 'casper' | 'icp' = 'solana') {
    switch (chainName) {
      case 'ethereum':
        this.signer = CivicSignProveFactory.createWithEthereumInIframe(instanceId, {
          timeout: CIVIC_SIGNER_TIMEOUT_MILLISECONDS,
          debug: true,
        });
        break;
      case 'casper':
        this.signer = CivicSignProveFactory.createWithCasperInIframe(instanceId, {
          timeout: CIVIC_SIGNER_TIMEOUT_MILLISECONDS,
          debug: true,
        });
        break;
      case 'icp':
        this.signer = CivicSignProveFactory.createWithIcpInIframe(instanceId, {
          timeout: CIVIC_SIGNER_TIMEOUT_MILLISECONDS,
          debug: true,
        });
        break;
      default:
        // We should consider not defaulting to solana, but throwing an error instead
        this.signer = CivicSignProveFactory.createWithSolanaInIframe(instanceId, {
          timeout: CIVIC_SIGNER_TIMEOUT_MILLISECONDS,
          debug: true,
        });
    }
  }

  withWallet(wallet: SolanaWalletAdapter): void {
    this.signer = CivicSignProveFactory.createWithSolanaWallet(wallet, {
      cluster: 'devnet',
      commitment: 'confirmed',
      recentBlockCheck: false,
      supportedClusterUrls: {
        devnet: clusterApiUrl('devnet'),
      },
      broadcastCheck: false,
    });
  }

  async requestDid(): Promise<Result<CustomError, AccountInfo>> {
    try {
      const didResult = await this.signer.requestDid();
      if (!didResult) {
        return Result.failed(new TechnicalError('Error retrieving DID from CivicSign. Result is undefined'));
      }
      return Result.successful(didResult);
    } catch (error) {
      return Result.failed(new TechnicalError('CivicSign requestDid error', error as Error));
    }
  }

  async requestProof(nonce?: Nonce): Promise<Result<CustomError, SignedProof>> {
    try {
      // an empty message will cause the RC to use a zero-cost tx instead of a signMessage tx
      const signedProof = await this.signer.requestProof(nonce?.nonce || '');
      if (!signedProof) {
        return Result.failed(new TechnicalError('Error retrieving proof from CivicSign. Result is undefined'));
      }
      return Result.successful(signedProof);
    } catch (error) {
      Log.error('CivicSign requestProof error', error as ErrorWithCode);
      const message = (error as Error)?.message || '';
      // Including the sign error message in the error message so it's filterable by Sentry
      if (message.includes('REQUEST_SIGNED_PROOF')) {
        return Result.failed(new TechnicalError(`CivicSign requestProof ${message}`, error as ErrorWithCode));
      }
      return Result.failed(new TechnicalError('CivicSign requestProof error', error as ErrorWithCode));
    }
  }

  async signMessage(message: Uint8Array): Promise<Result<CustomError, Uint8Array>> {
    try {
      const signedMessage = await this.signer.signMessage(message);
      if (!signedMessage) {
        return Result.failed(new TechnicalError('Error retrieving signed message from CivicSign. Result is undefined'));
      }
      return Result.successful(signedMessage);
    } catch (error) {
      return Result.failed(new TechnicalError('CivicSign signMessage error', error as Error));
    }
  }
}
